import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { API_URL, createAxiosInstance } from '../../../api';

const RenewCourseTab = React.memo(() => {
  const theme = useTheme();
  const userId = useSelector((state) => state.main.userId);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renewing, setRenewing] = useState(false);

  // Fetch subscription info
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/status/${userId}`);
        
        setSubscription(response.data);
        
      } catch (error) {
        console.error('Error fetching subscription:', error);
        setError('Failed to load subscription information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [userId]);

  const handleRenew = async () => {
    setRenewing(true);
    try {
      // Add renewal logic here
      console.log('Renewing subscription...');
    } catch (error) {
      console.error('Error renewing subscription:', error);
    } finally {
      setRenewing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        height: '400px',
        gap: 2
      }}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading subscription information...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Renew Course Subscription
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your course subscription and renewal options
        </Typography>
      </Box>

      {/* Subscription Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <RefreshIcon sx={{ color: theme.palette.primary.main, mr: 2, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Current Subscription
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subscription?.membership || 'Monthly Plan'}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              <Chip 
                label={subscription?.status || 'Active'} 
                size="small" 
                color={subscription?.status === 'active' ? 'success' : 'default'}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Price:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                £{subscription?.price || '2.00'}/month
              </Typography>
            </Box>

            {subscription?.nextBilling && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Next Billing:
                </Typography>
                <Typography variant="body2">
                  {new Date(subscription.nextBilling).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              startIcon={renewing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={handleRenew}
              disabled={renewing}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {renewing ? 'Renewing...' : 'Renew Subscription'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card sx={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 2
          }}>
            <ScheduleIcon sx={{ color: theme.palette.primary.main, mr: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Renewal Information
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your subscription will automatically renew on the next billing date. 
            You can manage your subscription settings in the Account Settings page.
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            Need help? Contact our support team for assistance with your subscription.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
});

RenewCourseTab.displayName = 'RenewCourseTab';

export default RenewCourseTab;
